<template>
    <div v-if="isSuperAdmin">
        <LaPageHeader heading="Fees"></LaPageHeader>

        <LaNoData v-if="isMultiYieldSelection" header="Select a publisher" description="Choose one in the navbar." />

        <template v-else>
            <div class="columns">
                <div class="column is-flex">
                    <div v-if="websitesComputed?.length > 0">
                        <LaBaseSelect
                            data-test="websites-select"
                            :items="websitesComputed"
                            v-model="selectedWebsite"
                            itemValue="id"
                            itemText="site_name"
                            :stretch="true"
                            :searchable="false"
                            :multiple="false"
                            @update:modelValue="handleInputWebsite(publisherSelectedComputed?.id, selectedWebsite.id)"
                        >
                        </LaBaseSelect>
                    </div>
                </div>

                <div class="column is-flex is-justify-content-right">
                    <LaBaseButton text="Add fee" icon="plus" data-test="add-fee" id="add-fee" @click="addFee()" />
                </div>
            </div>

            <div class="columns">
                <div class="column" v-if="feesComputed">
                    <FeeCard :fees="feesComputed" />
                </div>
            </div>
        </template>

        <LaDialog name="feeForm">
            <FeeForm />
        </LaDialog>

        <LaDialog name="feeDelete">
            <LaDeleteConfirmDialog item-name="fee" item-property="website" item-property2="valid_from_isoformat" />
        </LaDialog>
    </div>
</template>

<script>
import LaPageHeader from "@/components/PageHeader";
import LaDialog from "@/components/dialog/Dialog";
import LaBaseButton from "@/components/button/BaseButton";
import LaBaseSelect from "@/components/inputs/BaseSelect";
import LaDeleteConfirmDialog from "@/components/dialog/DeleteConfirmDialog";
import FeeForm from "@/components/dialog/FeeForm";
import FeeCard from "@/components/fees/FeeCard";
import LaNoData from "@/components/NoData";
import store from "@/store/store";
import { mutationTypes as dialogMutationTypes } from "@/store/Dialog/types";
import { computed, ref, watch } from "vue";
import { getterTypes } from "@/store/LoggedUser/types";
import { getterTypes as publisherGetterTypes } from "@/store/Publisher/types";
import { actionTypes as websiteActionTypes, getterTypes as websiteGetterTypes } from "@/store/Websites/types";
import { actionTypes as feeActionTypes, getterTypes as feeGetterTypes } from "@/store/Fees/types";

export default {
    name: "FeeView",
    components: {
        LaPageHeader,
        FeeCard,
        FeeForm,
        LaBaseSelect,
        LaDialog,
        LaBaseButton,
        LaDeleteConfirmDialog,
        LaNoData,
    },
    setup() {
        const selectedWebsite = ref({ id: "all", site_name: "all" });

        const userRights = computed(() => store.getters[getterTypes.getUserRights]);
        const isSuperAdmin = computed(() => userRights.value && userRights.value["super_admin"]);

        const publisherSelectedComputed = computed(() => store.getters[publisherGetterTypes.getSelectedPublisher]);

        // Fees are per-publisher; yield multi-select has no sensible scope here, so
        // show a prompt to pick a single publisher instead of a confusing first-only view.
        const isMultiYieldSelection = computed(() => {
            if (store.getters[publisherGetterTypes.getSelectedMode] !== "yield") return false;
            const selected = store.getters[publisherGetterTypes.getSelectedPublishersYield] || [];
            return selected.length > 1;
        });

        const websiteSelectedComputed = computed(() => store.getters[feeGetterTypes.getWebsiteSelected]);
        if (!websiteSelectedComputed.value) {
            store.dispatch(feeActionTypes.selectWebsite, "all");
        }

        const websitesComputed = computed(() => store.getters[websiteGetterTypes.getWebsitesByPublisher]);

        const addFee = () => {
            store.commit(dialogMutationTypes.openDialog, {
                dialogName: "feeForm",
                dialogHeading: "Add fee",
            });
        };

        const feesComputed = computed(() => store.getters[feeGetterTypes.getFees]);

        // Refetch websites + fees whenever the selected publisher changes
        // (e.g. user toggles ltba/yield mode or picks a different publisher).
        // Reset website filter to "all" so stale per-website filters don't leak across publishers.
        const loadForPublisher = (publisherId) => {
            store.dispatch(websiteActionTypes.getDataByPublisher, { publisherId });
            store.dispatch(feeActionTypes.selectWebsite, "all");
            store.dispatch(feeActionTypes.getAll, {
                publisherId,
                websiteId: "all",
            });
            selectedWebsite.value = { id: "all", site_name: "all" };
        };

        watch(
            () => publisherSelectedComputed.value?.id,
            (id) => {
                if (id) loadForPublisher(id);
            },
            { immediate: true }
        );

        const handleInputWebsite = (publisherId, websiteId) => {
            store.dispatch(feeActionTypes.getAll, {
                publisherId: publisherId,
                websiteId: websiteId,
                refreshPaginator: true,
            });
            store.dispatch(feeActionTypes.selectWebsite, websiteId);
        };

        return {
            isSuperAdmin,
            selectedWebsite,
            feesComputed,
            websitesComputed,
            publisherSelectedComputed,
            websiteSelectedComputed,
            isMultiYieldSelection,
            addFee,
            handleInputWebsite,
        };
    },
};
</script>

<style scoped></style>
